/**
 * Fase 5 — Kurs-veiviser steg 4: Sensor-tildeling.
 *
 * Multi-select fra rolle-filtrert sensor-liste. Inline-opprette-knapp åpner modal.
 * Habilitet-sjekk: hvis valgt sensor har samme member_id som kurset → gul advarsel
 * + obligatorisk begrunnelse ved tildeling. Selv-tildeling blokkert.
 */
import { useEffect, useState } from 'react';
import { Loader2, UserPlus, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { authenticatedFetch, getUser } from '../../../lib/auth';
import type { CourseWizardData } from '../NewCourseInstanceWizard';
import InlineCreateSensorModal from './InlineCreateSensorModal';
import InlineHelpTerm from '../InlineHelpTerm';

interface SensorRow {
  id: string;
  full_name: string;
  email: string;
  role: string;
  is_sensor?: boolean;
  is_hybrid?: boolean;
  is_member_admin?: boolean;
  member_id?: string | null;
  assigned_courses_count?: number;
}

export interface SensorStepProps {
  data: CourseWizardData;
  patch: (updates: Partial<CourseWizardData>) => void;
}

export default function SensorStep({ data, patch }: SensorStepProps) {
  const [sensors, setSensors] = useState<SensorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const u = await getUser();
        setCurrentUserId(u?.id ?? null);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedFetch('/api/admin/sensors');
        const json = await res.json();
        if (json.success && Array.isArray(json.sensors)) setSensors(json.sensors);
        else if (Array.isArray(json)) setSensors(json);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Kunne ikke laste sensorer');
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshKey]);

  const toggleSensor = (sensor: SensorRow) => {
    // Blokker selv-tildeling (uansett rolle)
    if (sensor.id === currentUserId) {
      setError('Du kan ikke tildele deg selv som sensor. En annen person må gjøre habilitet-vurderingen.');
      return;
    }
    setError(null);

    const idx = data.selected_sensors.findIndex((s) => s.id === sensor.id);
    if (idx >= 0) {
      patch({
        selected_sensors: data.selected_sensors.filter((_, i) => i !== idx),
      });
    } else {
      // Habilitet-trigger: KUN hvis sensoren er hybrid (dvs. en medlem/admin i din
      // organisasjon med sensor-tilgang i tillegg). Uavhengige fagpersoner opprettet
      // som ren sensor-rolle via wizarden er habilitet-nøytrale selv om de er knyttet
      // til din org i sensor-poolen.
      const isEmployeeInSameOrg = !!(sensor.is_hybrid && sensor.member_id && data.member_id && sensor.member_id === data.member_id);
      patch({
        selected_sensors: [
          ...data.selected_sensors,
          {
            id: sensor.id,
            full_name: sensor.full_name,
            email: sensor.email,
            same_org: isEmployeeInSameOrg,
            habilitet_reason: '',
          },
        ],
      });
    }
  };

  const updateReason = (sensorId: string, reason: string) => {
    patch({
      selected_sensors: data.selected_sensors.map((s) =>
        s.id === sensorId ? { ...s, habilitet_reason: reason } : s,
      ),
    });
  };

  const needsReason = data.selected_sensors.some((s) => s.same_org && (s.habilitet_reason || '').trim().length < 20);

  return (
    <div className="space-y-5">
      <div className="rounded-lg bg-kf-gray-50 border border-kf-gray-200 p-4 text-sm text-kf-gray-700 flex items-start gap-2">
        <Info className="w-4 h-4 text-kf-gray-500 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-kf-gray-800 mb-1">Hvorfor tildele sensor allerede nå?</p>
          <p>
            Sensorer er nødvendige for å kunne gjennomføre sensur av prøven. Du kan tildele flere sensorer. Sensoren får
            e-postvarsel ved første innlevering. Hvis du ikke tildeler nå kan du gjøre det senere i Sensorer-fanen, men
            eksamen kan ikke sensureres uten minst én tildelt sensor.
          </p>
        </div>
      </div>

      {/* Valgt-liste med evt. habilitet-begrunnelser */}
      {data.selected_sensors.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-kf-gray-900 mb-2">
            Valgte sensorer ({data.selected_sensors.length})
          </h4>
          <div className="space-y-2">
            {data.selected_sensors.map((s) => (
              <div
                key={s.id}
                className={`rounded-lg border p-3 ${
                  s.same_org ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-kf-gray-900 text-sm">{s.full_name}</div>
                    <div className="text-xs text-kf-gray-600">{s.email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleSensor({ id: s.id } as SensorRow)}
                    className="text-red-600 hover:text-red-800 text-xs"
                  >
                    Fjern
                  </button>
                </div>
                {s.same_org && (
                  <div className="mt-3 border-t border-amber-200 pt-3">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-900 font-medium">
                        <InlineHelpTerm
                          slug="wizard-habilitet-sensor-same-org"
                          label="Habilitet"
                          route="/courses-and-exams"
                        />
                        : Sensor er i samme medlemsorganisasjon som kurset. Begrunn hvorfor
                        dette er forsvarlig (minst 20 tegn — vil loggføres).
                      </p>
                    </div>
                    <textarea
                      value={s.habilitet_reason || ''}
                      onChange={(e) => updateReason(s.id, e.target.value)}
                      rows={2}
                      placeholder="F.eks: Sensor er faglig leder og er den eneste med nødvendig fagkompetanse i organisasjonen for dette tema…"
                      className={`w-full px-2 py-1.5 text-sm border rounded bg-white ${
                        (s.habilitet_reason || '').trim().length >= 20
                          ? 'border-emerald-400'
                          : 'border-amber-400'
                      }`}
                    />
                    <div className={`text-xs mt-1 ${
                      (s.habilitet_reason || '').trim().length >= 20 ? 'text-emerald-700' : 'text-amber-700'
                    }`}>
                      {(s.habilitet_reason || '').trim().length} / 20 tegn
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {needsReason && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            En eller flere valgte sensorer trenger habilitet-begrunnelse (minst 20 tegn). Lagringen vil blokkeres til
            dette er fylt ut.
          </span>
        </div>
      )}

      {/* Sensor-liste */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-kf-gray-900">Tilgjengelige sensorer</h4>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-kf-blue-700 hover:bg-kf-blue-50 rounded border border-kf-blue-200"
          >
            <UserPlus className="w-4 h-4" />
            Legg til sensor
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800 mb-2">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-kf-gray-600 py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Laster sensorer…
          </div>
        ) : sensors.length === 0 ? (
          <div className="rounded-md bg-kf-blue-50 border border-kf-blue-200 px-3 py-2 text-sm text-kf-blue-900">
            Ingen sensorer tilgjengelig. Klikk "Opprett ny sensor" for å invitere en.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto border border-kf-gray-200 rounded-lg divide-y divide-kf-gray-100">
            {sensors.map((s) => {
              const selected = data.selected_sensors.some((x) => x.id === s.id);
              const isSelf = s.id === currentUserId;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleSensor(s)}
                  disabled={isSelf}
                  className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors ${
                    selected ? 'bg-emerald-50' : 'bg-white hover:bg-kf-gray-50'
                  } ${isSelf ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="shrink-0">
                    {selected ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-kf-gray-300" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-kf-gray-900 text-sm">{s.full_name}</span>
                      {s.is_hybrid && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200">
                          Hybrid
                        </span>
                      )}
                      {isSelf && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                          Deg selv (blokkert)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-kf-gray-500">
                      {s.email}
                      {typeof s.assigned_courses_count === 'number'
                        ? ` · ${s.assigned_courses_count} tildelte kurs`
                        : ''}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <InlineCreateSensorModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        memberIdForContext={data.member_id || undefined}
        onCreated={(newSensor) => {
          // Trigg refetch av sensor-lista
          setRefreshKey((k) => k + 1);
          // Auto-velg den nyopprettede sensoren — bruker har allerede uttrykt intensjon ved å
          // opprette hen i veiviserens sensor-step. Habilitet-sjekk: samme-org trigges hvis
          // backend returnerte member_id som matcher kurset.
          if (newSensor?.id) {
            const sameOrg = !!(newSensor.member_id && data.member_id && newSensor.member_id === data.member_id);
            // Unngå dobbeltvalg hvis bruker trigger samme opprettelse to ganger
            if (!data.selected_sensors.some((s) => s.id === newSensor.id)) {
              patch({
                selected_sensors: [
                  ...data.selected_sensors,
                  {
                    id: newSensor.id,
                    full_name: newSensor.full_name,
                    email: newSensor.email,
                    same_org: sameOrg,
                    habilitet_reason: '',
                  },
                ],
              });
            }
          }
        }}
      />
    </div>
  );
}
